import React, { useState, useRef, useEffect } from "react";

const DEFAULT_PROMPTS = [
  { count: 5, sense: "things you can see",  hint: "Look around. Tap a circle for each thing you notice." },
  { count: 4, sense: "things you can touch", hint: "Feel the textures around you. Tap for each one." },
  { count: 3, sense: "things you can hear",  hint: "Listen closely. Tap for each sound you notice." },
  { count: 2, sense: "things you can smell", hint: "Breathe in. Tap for each scent you detect." },
  { count: 1, sense: "thing you can taste",  hint: "Notice any taste in your mouth. Tap when ready." },
];

export default function GroundingTemplate({ config = {}, onProgress, onComplete }) {
  const prompts = config.prompts?.length ? config.prompts : DEFAULT_PROMPTS;

  const [stepIndex, setStepIndex] = useState(0);
  const [tapped, setTapped]       = useState(0);
  const completeTimerRef          = useRef(null);

  const done = stepIndex >= prompts.length;
  const step = prompts[stepIndex] || prompts[prompts.length - 1];

  useEffect(() => () => clearTimeout(completeTimerRef.current), []);

  const handleTap = () => {
    const next = tapped + 1;
    if (next >= step.count) {
      const nextStep = stepIndex + 1;
      setTapped(0);
      setStepIndex(nextStep);
      if (nextStep >= prompts.length) {
        onProgress?.(1, "Complete");
        completeTimerRef.current = setTimeout(() => onComplete?.(), 1000);
      } else {
        onProgress?.(nextStep / prompts.length, `Sense ${nextStep + 1} of ${prompts.length}`);
      }
    } else {
      setTapped(next);
    }
  };

  // Sense progress dots
  const senseDots = prompts.map((_, i) => {
    if (i < stepIndex)  return "done";
    if (i === stepIndex) return "active";
    return "inactive";
  });

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-5">
      {/* Sense step dots */}
      <div className="tw-flex tw-gap-2.5">
        {senseDots.map((state, i) => (
          <div
            key={i}
            className="tw-rounded-full tw-transition-all tw-duration-300"
            style={{
              width: 8, height: 8,
              background:
                state === "done"   ? "rgba(100,200,100,0.7)" :
                state === "active" ? "#CB5A3C" :
                "rgba(255,255,255,0.15)",
              transform: state === "active" ? "scale(1.3)" : "scale(1)",
            }}
          />
        ))}
      </div>

      {/* Big count + sense label */}
      <div className="tw-text-center">
        <p className="tw-font-bold tw-leading-none tw-mb-2 tw-transition-all tw-duration-300"
          style={{ fontSize: 80, color: "#CB5A3C" }}>
          {done ? "✓" : step.count}
        </p>
        <p className="tw-text-2xl tw-font-light tw-mb-2" style={{ color: "rgba(255,255,255,0.9)" }}>
          {done ? "Fully grounded" : step.sense}
        </p>
        {!done && (
          <p className="tw-text-[15px] tw-text-center tw-max-w-[280px] tw-leading-relaxed"
            style={{ color: "rgba(255,255,255,0.5)" }}>
            {step.hint}
          </p>
        )}
      </div>

      {/* Tap circles */}
      {!done && (
        <div className="tw-flex tw-gap-3 tw-flex-wrap tw-justify-center tw-mb-2">
          {Array.from({ length: step.count }).map((_, i) => {
            const filled = i < tapped;
            const isNext = i === tapped;
            return (
              <button
                key={i}
                onClick={isNext ? handleTap : undefined}
                disabled={!isNext}
                className="tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-lg tw-transition-all tw-duration-300"
                style={{
                  width: 44, height: 44,
                  background: filled ? "rgba(203,90,60,0.6)" : "rgba(255,255,255,0.06)",
                  border: `2px solid ${filled ? "#CB5A3C" : isNext ? "rgba(91,63,214,0.5)" : "rgba(26,26,26,0.2)"}`,
                  boxShadow: filled ? "0 0 12px rgba(203,90,60,0.3)" : "none",
                  cursor: isNext ? "pointer" : "default",
                  color: filled ? "white" : "transparent",
                }}
                aria-label={filled ? "Tapped" : isNext ? "Tap" : ""}
              >
                {filled ? "✓" : ""}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
